import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Evaluation } from '@/types/evaluation.types';
import { generateDemoScorecards } from '@/services/demo-data';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Wand2 } from 'lucide-react';

interface DemoDataGeneratorDialogProps {
  evaluation: Evaluation;
  onGenerate?: () => void;
}

/**
 * Dialog for generating demo scorecard data for testing/demo purposes
 * Allows selecting participants and choosing between empty or random score generation
 */
export function DemoDataGeneratorDialog({ evaluation, onGenerate }: DemoDataGeneratorDialogProps) {
  const { t } = useTranslation('dashboard');
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'empty' | 'random'>('random');
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);

  const handleParticipantToggle = (participantId: string, checked: boolean) => {
    setSelectedParticipantIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(participantId);
      } else {
        newSet.delete(participantId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedParticipantIds(new Set(evaluation.participants.map((p) => p.id)));
  };

  const handleSelectNone = () => {
    setSelectedParticipantIds(new Set());
  };

  const handleGenerate = () => {
    setIsGenerating(true);

    // Generate scorecards
    const count = generateDemoScorecards(
      evaluation,
      Array.from(selectedParticipantIds),
      mode
    );

    console.log(`Generated ${count} demo scorecards`);

    setIsGenerating(false);
    setOpen(false);

    // Reset selection
    setSelectedParticipantIds(new Set());
    setMode('random');

    // Notify parent to refresh
    onGenerate?.();
  };

  const canGenerate = selectedParticipantIds.size > 0 && evaluation.targetSolutions.length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Wand2 className="mr-2 h-4 w-4" />
          {t('demoData.buttonLabel')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('demoData.title')}</DialogTitle>
          <DialogDescription>{t('demoData.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Participant Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base">{t('demoData.selectParticipants')}</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  className="h-7 text-xs"
                >
                  {t('demoData.selectAll')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectNone}
                  className="h-7 text-xs"
                >
                  {t('demoData.selectNone')}
                </Button>
              </div>
            </div>

            {evaluation.participants.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('demoData.noParticipants')}</p>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-md p-3">
                {evaluation.participants.map((participant) => (
                  <div key={participant.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`participant-${participant.id}`}
                      checked={selectedParticipantIds.has(participant.id)}
                      onCheckedChange={(checked) =>
                        handleParticipantToggle(participant.id, checked === true)
                      }
                    />
                    <label
                      htmlFor={`participant-${participant.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {participant.name}
                      <span className="text-muted-foreground ml-2">
                        ({participant.participantType})
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mode Selection */}
          <div className="space-y-3">
            <Label className="text-base">{t('demoData.selectMode')}</Label>
            <RadioGroup value={mode} onValueChange={(value) => setMode(value as 'empty' | 'random')}>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="random" id="mode-random" />
                <div className="space-y-1">
                  <label
                    htmlFor="mode-random"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {t('demoData.randomMode')}
                  </label>
                  <p className="text-xs text-muted-foreground">{t('demoData.randomModeDescription')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="empty" id="mode-empty" />
                <div className="space-y-1">
                  <label
                    htmlFor="mode-empty"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {t('demoData.emptyMode')}
                  </label>
                  <p className="text-xs text-muted-foreground">{t('demoData.emptyModeDescription')}</p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Summary */}
          {canGenerate && (
            <div className="rounded-md bg-muted p-3 text-sm">
              <p className="text-muted-foreground">
                {t('demoData.willGenerate', {
                  count: selectedParticipantIds.size * evaluation.targetSolutions.length,
                  participants: selectedParticipantIds.size,
                  solutions: evaluation.targetSolutions.length,
                })}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('common:actions.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleGenerate} disabled={!canGenerate || isGenerating}>
            <Wand2 className="mr-2 h-4 w-4" />
            {isGenerating ? t('demoData.generating') : t('demoData.generate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
